import OpenInNewIcon from '@material-design-icons/svg/filled/open_in_new.svg?react';
import RateReviewIcon from '@material-design-icons/svg/filled/rate_review.svg?react';
import RefreshIcon from '@material-design-icons/svg/filled/refresh.svg?react';
import { useCallback, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Button } from '../components/Button';
import { DataTable } from '../components/common/DataTable';
import { Pagination } from '../components/common/Pagination';
import {
  type QueryLoadContext,
  usePaginatedQueryLoader,
} from '../hooks/usePaginatedQueryLoader';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { useTRPC } from '../hooks/useTRPC';
import { isLeftResult, isRightResult } from '../lib/trpcTypeGuards';
import type { RightResult, RouterOutputs } from '../types/TRPC';
import { captureAndReportError } from '../utils/errorReporting';
import { getAdminDappPath } from '../utils/dappRoutes';
import { hasSuperadminAccess } from '../utils/roleUtils';
import { formatDate } from '../utils/stringHelpers';

const PAGE_SIZE = 25;

type ReviewRepliesResult = RightResult<
  RouterOutputs['listPublisherReviewReplies']
>;
type PublisherReviewReply = ReviewRepliesResult['replies'][number];

function formatReplyDate(value: string) {
  return formatDate(
    value,
    {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    },
    {
      formatter: 'datetime',
    },
  );
}

function ReplyText({ children }: { children: string | null }) {
  return (
    <p className="max-h-20 overflow-hidden break-words text-sm leading-5 text-neutral-300">
      {children || 'No reply text'}
    </p>
  );
}

export function PublisherReviewRepliesView() {
  const { authState, canRender } = useRequireAuth({
    requireRole: 'superadmin',
  });
  const trpc = useTRPC();

  const [replies, setReplies] = useState<PublisherReviewReply[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1,
  });

  const canAccessPage =
    authState.type === 'Authenticated' &&
    hasSuperadminAccess(authState.user.role, authState.user.email);

  const loadReplies = useCallback(
    async ({ isCurrentRequest }: QueryLoadContext) => {
      try {
        const result = await trpc.listPublisherReviewReplies.query({
          page: currentPage,
          limit: PAGE_SIZE,
        });

        if (!isCurrentRequest()) {
          return;
        }

        if (isRightResult(result)) {
          setReplies(result.right.replies);
          setPagination(result.right.pagination);
          setCurrentPage(result.right.pagination.page);
        } else if (isLeftResult(result)) {
          toast.error(result.left.message);
        }
      } catch (error) {
        if (!isCurrentRequest()) {
          return;
        }

        captureAndReportError(error, {
          operation: 'superadmin.loadPublisherReviewReplies',
          component: 'PublisherReviewRepliesView',
        });
        toast.error('Failed to load publisher review replies');
      }
    },
    [currentPage, trpc],
  );

  const { reload } = usePaginatedQueryLoader({
    enabled: canRender && canAccessPage,
    setLoading: setIsLoading,
    loadPage: loadReplies,
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <RateReviewIcon className="size-5 fill-current text-teal-300" />
            <h2 className="text-xl font-semibold text-white">Review Replies</h2>
          </div>
          <div className="mt-2 flex flex-wrap gap-3 text-sm text-neutral-400">
            <span>{pagination.total} replies</span>
          </div>
        </div>

        <Button
          type="button"
          variant="secondary"
          onClick={() => void reload()}
          submitting={isLoading}
          disabled={isLoading}
          className="min-w-[140px]"
        >
          {!isLoading && (
            <RefreshIcon className="size-4 fill-current text-neutral-900" />
          )}
          Refresh
        </Button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-neutral-700 bg-neutral-800/50">
        <div className="border-b border-neutral-700 px-6 py-5">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">
              Publisher Replies
            </h3>
            <div className="text-sm text-neutral-400">
              Page {pagination.page} of {pagination.totalPages}
            </div>
          </div>
        </div>

        <DataTable
          data={replies}
          columns={[
            {
              key: 'dapp',
              header: 'Dapp',
              size: 220,
              accessor: (reply) => (
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-white">
                    {reply.dappName}
                  </div>
                  <div className="truncate text-xs text-neutral-400">
                    {reply.androidPackage}
                  </div>
                </div>
              ),
            },
            {
              key: 'publisher',
              header: 'Publisher',
              size: 200,
              accessor: (reply) => (
                <div className="min-w-0">
                  <div className="truncate text-sm text-white">
                    {reply.publisherName}
                  </div>
                  {reply.actorName && (
                    <div className="truncate text-xs text-neutral-500">
                      Replied by {reply.actorName}
                    </div>
                  )}
                  {reply.portalPublisherName &&
                    reply.portalPublisherName !== reply.publisherName &&
                    !reply.actorName && (
                      <div className="truncate text-xs text-neutral-500">
                        {reply.portalPublisherName}
                      </div>
                    )}
                </div>
              ),
            },
            {
              key: 'reply',
              header: 'Reply',
              size: 320,
              accessor: (reply) => <ReplyText>{reply.reply}</ReplyText>,
            },
            {
              key: 'reviewId',
              header: 'Review ID',
              size: 180,
              accessor: (reply) => (
                <code className="block truncate text-xs text-neutral-400">
                  {reply.reviewId}
                </code>
              ),
            },
            {
              key: 'updated',
              header: 'Updated',
              size: 160,
              accessor: (reply) => (
                <span className="text-sm text-neutral-400">
                  {formatReplyDate(reply.replyUpdatedAt)}
                </span>
              ),
            },
            {
              key: 'actions',
              header: '',
              size: 72,
              accessor: (reply) => (
                <Link
                  to={getAdminDappPath({
                    id: reply.dappId,
                    packageName: reply.androidPackage,
                  })}
                  className="inline-flex rounded-lg p-2 text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-white"
                  title="Open dapp"
                  aria-label={`Open ${reply.dappName}`}
                >
                  <OpenInNewIcon className="size-4 fill-current" />
                </Link>
              ),
            },
          ]}
          isLoading={isLoading}
          emptyMessage="No publisher review replies found"
          skeletonRowCount={6}
          renderMobileItem={(reply) => (
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-white">
                    {reply.dappName}
                  </div>
                  <div className="truncate text-xs text-neutral-400">
                    {reply.androidPackage}
                  </div>
                </div>
                <Link
                  to={getAdminDappPath({
                    id: reply.dappId,
                    packageName: reply.androidPackage,
                  })}
                  className="shrink-0 rounded-lg p-1 text-neutral-400 transition-colors hover:text-white"
                  title="Open dapp"
                  aria-label={`Open ${reply.dappName}`}
                >
                  <OpenInNewIcon className="size-5 fill-current" />
                </Link>
              </div>

              <div className="text-xs text-neutral-400">
                <span className="text-neutral-500">Publisher:</span>{' '}
                <span className="text-neutral-300">{reply.publisherName}</span>
              </div>

              {reply.actorName && (
                <div className="text-xs text-neutral-400">
                  <span className="text-neutral-500">Replied by:</span>{' '}
                  <span className="text-neutral-300">{reply.actorName}</span>
                </div>
              )}

              <div>
                <div className="mb-1 text-xs text-neutral-500">Reply</div>
                <ReplyText>{reply.reply}</ReplyText>
              </div>

              <div className="text-xs text-neutral-400">
                <span className="text-neutral-500">Review ID:</span>{' '}
                <code className="text-neutral-300">{reply.reviewId}</code>
              </div>

              <div className="text-xs text-neutral-400">
                Updated {formatReplyDate(reply.replyUpdatedAt)}
              </div>
            </div>
          )}
        />

        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
}
